// F-MOTOR-RESERVAS.3.1.4 — Fluxo público de pré-reserva em rota única.
// Decisões aprovadas: D1=A (sem GET público), D2=A (steps em /reservar/:quartoId),
// D3=A (seletor de café), D4=A (CPF com DV), D5=A (observações 500), D6=B ("em breve").
// Backend é fonte autoritativa — frontend NUNCA calcula preço.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format, differenceInCalendarDays, addDays, isBefore, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowLeft, ArrowRight, Calendar as CalendarIcon, Minus, Plus,
  Loader2, CheckCircle2, AlertTriangle, Users, Info,
} from "lucide-react";
import { getQuarto, getPousada, postCotacao, postReserva } from "@/lib/publicApi";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CTAWhatsApp } from "@/components/public/CTAWhatsApp";
import { brl } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { maskCPF, maskTelefone, onlyDigits, isValidCPF, isValidEmail, isValidTelefone }
  from "@/lib/masks";
import { useIdempotencyKey } from "@/lib/idempotency";
import { RESERVAR_TESTIDS as T } from "@/constants/testIds/reservar";

const CAFE_OPTS = [
  { value: "nenhum", label: "Sem café da manhã" },
  { value: "simples", label: "Café da manhã simples" },
  { value: "donna_italia", label: "Café da manhã Donna Itália" },
];

const isoDate = (d) => format(d, "yyyy-MM-dd");

export default function Reservar() {
  const { quartoId } = useParams();
  const navigate = useNavigate();
  const idemKey = useIdempotencyKey();

  const [quarto, setQuarto] = useState(null);
  const [pousada, setPousada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [numPessoas, setNumPessoas] = useState(1);
  const [cafeTipo, setCafeTipo] = useState("nenhum");
  const [step, setStep] = useState(1);
  const [step1Err, setStep1Err] = useState("");

  const [cotacao, setCotacao] = useState(null);
  const [cotacaoLoading, setCotacaoLoading] = useState(false);

  const [form, setForm] = useState({
    nome: "", cpf: "", email: "", telefone: "",
    observacoes: "", website: "", aceite: false,
  });
  const [formErr, setFormErr] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  const [reserva, setReserva] = useState(null);
  const [unavailable, setUnavailable] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const q = await getQuarto(quartoId);
        if (cancel) return;
        setQuarto(q);
        if (q.pousada_id) {
          const p = await getPousada(q.pousada_id).catch(() => null);
          if (!cancel) setPousada(p);
        }
      } catch (err) {
        if (!cancel && err?.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [quartoId]);

  const diarias = useMemo(() => {
    if (!range.from || !range.to) return 0;
    return differenceInCalendarDays(range.to, range.from);
  }, [range]);

  const ctxWa = {
    type: "quarto",
    numero: quarto?.numero,
    pousadaNome: pousada?.nome,
  };

  // ---- Step 1 → Step 2: busca cotação ----
  const avancarParaCotacao = async () => {
    setStep1Err("");
    setUnavailable(null);
    if (!range.from || !range.to) {
      setStep1Err("Selecione as datas de entrada e saída."); return;
    }
    if (diarias < 1) {
      setStep1Err("A data de saída deve ser posterior à de entrada."); return;
    }
    const hoje = startOfDay(new Date());
    if (isBefore(range.from, hoje)) {
      setStep1Err("A data de entrada não pode ser no passado."); return;
    }
    if (numPessoas < 1) {
      setStep1Err("Informe pelo menos 1 hóspede."); return;
    }
    if (quarto && numPessoas > quarto.capacidade) {
      setStep1Err(`Capacidade máxima deste quarto: ${quarto.capacidade} hóspede(s).`);
      return;
    }
    setCotacaoLoading(true);
    try {
      const data = await postCotacao({
        quarto_id: quartoId,
        data_entrada: isoDate(range.from),
        data_saida: isoDate(range.to),
        num_pessoas: numPessoas,
        cafe_tipo: cafeTipo,
      });
      if (!data.disponivel) {
        setUnavailable({ motivo: data.motivo, detalhe: data.detalhe });
      } else {
        setCotacao(data);
        setStep(2);
      }
    } catch (err) {
      const sc = err?.response?.status;
      const msg = err?.response?.data?.detail || "Falha ao calcular a cotação.";
      if (sc === 404) setNotFound(true);
      else setStep1Err(msg);
    } finally {
      setCotacaoLoading(false);
    }
  };

  // ---- Step 2 → submit da pré-reserva ----
  const validateForm = () => {
    const e = {};
    if (!form.nome.trim() || form.nome.trim().length < 2)
      e.nome = "Informe seu nome completo.";
    if (!isValidCPF(form.cpf)) e.cpf = "CPF inválido.";
    if (!isValidEmail(form.email)) e.email = "E-mail inválido.";
    if (!isValidTelefone(form.telefone)) e.telefone = "Telefone inválido.";
    if ((form.observacoes || "").length > 500)
      e.observacoes = "Máximo de 500 caracteres.";
    if (!form.aceite) e.aceite = "Confirme que leu as condições da pré-reserva.";
    setFormErr(e);
    return Object.keys(e).length === 0;
  };

  const enviarReserva = async () => {
    setSubmitErr("");
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        quarto_id: quartoId,
        data_entrada: isoDate(range.from),
        data_saida: isoDate(range.to),
        num_pessoas: numPessoas,
        cafe_tipo: cafeTipo,
        hospede: {
          nome: form.nome.trim(),
          cpf: onlyDigits(form.cpf),
          email: form.email.trim(),
          telefone: onlyDigits(form.telefone),
        },
        observacoes: form.observacoes.trim(),
        website: form.website, // honeypot — deve ir vazio
      };
      const data = await postReserva(payload, { idempotencyKey: idemKey });
      setReserva(data);
      setStep(3);
    } catch (err) {
      const sc = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (sc === 409) {
        setUnavailable({
          motivo: "conflito_reserva",
          detalhe: detail || "O quarto acabou de ficar indisponível neste período.",
        });
      } else if (sc === 429) {
        setSubmitErr("Muitas tentativas. Aguarde cerca de 1 minuto e tente novamente.");
      } else if (sc === 400 || sc === 422) {
        setSubmitErr(detail || "Verifique os dados preenchidos.");
      } else if (sc === 404) {
        setNotFound(true);
      } else {
        setSubmitErr("Não conseguimos enviar sua pré-reserva. Tente novamente ou fale no WhatsApp.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Loading / 404 ----------
  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-16">
        <div className="h-8 w-48 bg-stone-200/60 rounded animate-pulse mb-6" />
        <div className="h-64 bg-stone-200/60 rounded-lg animate-pulse" />
      </div>
    );
  }
  if (notFound || !quarto) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] mb-3 font-semibold">404</div>
        <h1 className="font-public-title text-3xl text-stone-900 mb-4">Quarto não encontrado</h1>
        <Link to="/pousadas" className="inline-flex items-center gap-2 text-sm text-[#083A6B] underline">
          <ArrowLeft size={14} /> Ver pousadas
        </Link>
      </div>
    );
  }

  // ---------- Render principal ----------
  return (
    <div data-testid={T.shell}>
      <Helmet>
        <title>{`Reservar ${quarto.numero}${pousada ? ` · ${pousada.nome}` : ""} · ${BRAND.name}`}</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content={`Pré-reserva online de ${quarto.numero}${pousada ? ` na ${pousada.nome}` : ""}.`} />
      </Helmet>

      <section className="max-w-4xl mx-auto px-5 lg:px-10 pt-8 lg:pt-10">
        <button
          type="button"
          data-testid={T.back}
          onClick={() => navigate(`/quartos/${quartoId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-[#083A6B] mb-5"
        >
          <ArrowLeft size={14} /> Voltar ao quarto
        </button>

        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold mb-2">
            Pré-reserva online
          </div>
          <h1 className="font-public-title text-3xl lg:text-4xl text-stone-900 leading-tight">
            {quarto.numero}
            {pousada && <span className="text-stone-500"> · {pousada.nome}</span>}
          </h1>
          <p className="text-sm text-stone-600 mt-2 flex items-center gap-1.5">
            <Users size={14} className="text-[#083A6B]" />
            Capacidade: {quarto.capacidade} {quarto.capacidade === 1 ? "hóspede" : "hóspedes"}
          </p>
        </div>

        {/* Stepper */}
        <div
          data-testid={T.stepIndicator}
          className="flex items-center gap-2 mb-10 text-xs"
        >
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center font-medium transition-colors ${
                  step >= n ? "bg-[#083A6B] text-white" : "bg-stone-200 text-stone-500"
                }`}
              >
                {n}
              </div>
              {n < 3 && <div className={`w-6 h-px ${step > n ? "bg-[#083A6B]" : "bg-stone-200"}`} />}
            </div>
          ))}
          <span className="ml-3 text-stone-600 uppercase tracking-wider">
            {step === 1 && "Datas e hóspedes"}
            {step === 2 && "Seus dados"}
            {step === 3 && "Pré-reserva enviada"}
          </span>
        </div>

        {unavailable && (
          <UnavailablePanel
            unavailable={unavailable}
            ctxWa={ctxWa}
            onVoltar={() => { setUnavailable(null); setStep(1); }}
          />
        )}

        {!unavailable && step === 1 && (
          <Step1
            quarto={quarto}
            range={range} setRange={setRange}
            numPessoas={numPessoas} setNumPessoas={setNumPessoas}
            cafeTipo={cafeTipo} setCafeTipo={setCafeTipo}
            diarias={diarias}
            error={step1Err}
            loading={cotacaoLoading}
            onAvancar={avancarParaCotacao}
          />
        )}

        {!unavailable && step === 2 && cotacao && (
          <Step2
            quarto={quarto}
            pousada={pousada}
            range={range}
            numPessoas={numPessoas}
            cotacao={cotacao}
            form={form} setForm={setForm}
            formErr={formErr}
            submitting={submitting}
            submitErr={submitErr}
            onVoltar={() => setStep(1)}
            onSubmit={enviarReserva}
            ctxWa={ctxWa}
          />
        )}

        {!unavailable && step === 3 && reserva && (
          <Step3
            reserva={reserva}
            quarto={quarto}
            pousada={pousada}
            ctxWa={ctxWa}
            onNovaReserva={() => navigate("/pousadas")}
          />
        )}
      </section>

      <div className="h-20" />
    </div>
  );
}

// =====================================================================
// STEP 1 — Datas, hóspedes e café
// =====================================================================
function Step1({ quarto, range, setRange, numPessoas, setNumPessoas,
                 cafeTipo, setCafeTipo, diarias, error, loading, onAvancar }) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const label = range.from && range.to
    ? `${format(range.from, "dd/MM/yyyy")} → ${format(range.to, "dd/MM/yyyy")}  ·  ${diarias} ${diarias === 1 ? "noite" : "noites"}`
    : range.from
      ? `Entrada: ${format(range.from, "dd/MM/yyyy")} · Selecione a saída`
      : "Selecione entrada e saída";

  const minus = () => setNumPessoas((n) => Math.max(1, n - 1));
  const plus = () => setNumPessoas((n) => Math.min(quarto.capacidade, n + 1));

  return (
    <div data-testid="reservar-step-1" className="rounded-xl border border-stone-200 bg-white p-6 lg:p-8 space-y-6">
      {/* Datas */}
      <div data-testid={T.dateRange}>
        <Label className="text-sm font-medium text-stone-700 mb-2 block">Datas da hospedagem</Label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={T.dateRangeTrigger}
              className="w-full inline-flex items-center justify-between gap-2 h-12 px-4 rounded-md border border-stone-300 bg-white text-sm text-stone-800 hover:border-[#083A6B]/40"
            >
              <span className="flex items-center gap-2">
                <CalendarIcon size={16} className="text-[#083A6B]" />
                {label}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white" align="start">
            <Calendar
              mode="range"
              selected={range}
              onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
              numberOfMonths={1}
              locale={ptBR}
              disabled={{ before: addDays(new Date(), 0) }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Hóspedes */}
      <div data-testid={T.guestsStepper}>
        <Label className="text-sm font-medium text-stone-700 mb-2 block">Número de hóspedes</Label>
        <div className="inline-flex items-center gap-3 rounded-md border border-stone-300 bg-white">
          <button
            type="button"
            data-testid={T.guestsMinus}
            onClick={minus}
            disabled={numPessoas <= 1}
            aria-label="Diminuir hóspedes"
            className="w-11 h-11 flex items-center justify-center text-stone-700 disabled:text-stone-300"
          >
            <Minus size={16} />
          </button>
          <span data-testid={T.guestsValue} aria-live="polite" className="min-w-[2rem] text-center text-base font-medium text-stone-900">
            {numPessoas}
          </span>
          <button
            type="button"
            data-testid={T.guestsPlus}
            onClick={plus}
            disabled={numPessoas >= quarto.capacidade}
            aria-label="Aumentar hóspedes"
            className="w-11 h-11 flex items-center justify-center text-stone-700 disabled:text-stone-300"
          >
            <Plus size={16} />
          </button>
        </div>
        <p className="text-xs text-stone-500 mt-2">Capacidade máxima deste quarto: {quarto.capacidade}.</p>
      </div>

      {/* Café */}
      <div>
        <Label htmlFor={T.cafeSelect} className="text-sm font-medium text-stone-700 mb-2 block">
          Café da manhã
        </Label>
        <select
          id={T.cafeSelect}
          data-testid={T.cafeSelect}
          value={cafeTipo}
          onChange={(e) => setCafeTipo(e.target.value)}
          className="w-full h-12 px-4 rounded-md border border-stone-300 bg-white text-sm text-stone-800 focus:border-[#083A6B]/60 focus:outline-none"
        >
          {CAFE_OPTS.map((o) => (
            <option key={o.value} value={o.value} data-testid={T.cafeSelectOption(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-stone-500 mt-2">
          O valor do café (por pessoa × diárias) é calculado automaticamente pela nossa equipe.
        </p>
      </div>

      {error && (
        <div
          data-testid={T.step1Error}
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
        >
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="pt-2 flex flex-col sm:flex-row sm:justify-end gap-3">
        <Button
          type="button"
          data-testid={T.verDisponibilidade}
          onClick={onAvancar}
          disabled={loading}
          className="h-12 px-6 bg-[#083A6B] hover:bg-[#052B50] text-white"
        >
          {loading ? (
            <><Loader2 size={16} className="animate-spin mr-2" /> Verificando…</>
          ) : (
            <>Ver cotação <ArrowRight size={14} className="ml-2" /></>
          )}
        </Button>
      </div>
    </div>
  );
}

// =====================================================================
// STEP 2 — Cotação + form hóspede
// =====================================================================
function Step2({ quarto, pousada, range, numPessoas, cotacao, form, setForm,
                formErr, submitting, submitErr, onVoltar, onSubmit, ctxWa }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid={T.step2} className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      {/* Resumo da cotação */}
      <aside
        data-testid={T.cotacaoSummary}
        className="lg:col-span-2 lg:order-2"
      >
        <div className="lg:sticky lg:top-24 rounded-xl border border-stone-200 bg-[#F5F1E8] p-6 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4A652] font-semibold">
              Resumo
            </div>
            <h2 className="font-public-title text-xl text-stone-900 mt-1">
              {quarto.numero}
              {pousada && <span className="text-stone-500 text-base"> · {pousada.nome}</span>}
            </h2>
          </div>

          <div className="text-sm space-y-2 text-stone-700">
            <div className="flex justify-between">
              <span>Entrada</span>
              <strong>{format(range.from, "dd/MM/yyyy")}</strong>
            </div>
            <div className="flex justify-between">
              <span>Saída</span>
              <strong>{format(range.to, "dd/MM/yyyy")}</strong>
            </div>
            <div className="flex justify-between">
              <span>Hóspedes</span>
              <strong>{numPessoas}</strong>
            </div>
            <div data-testid={T.cotacaoDiarias} className="flex justify-between">
              <span>Diárias</span>
              <strong>{cotacao.diarias} × {brl(cotacao.valor_diaria)}</strong>
            </div>
          </div>

          <hr className="border-stone-300/60" />

          <div className="text-sm space-y-2 text-stone-700">
            <div data-testid={T.cotacaoSubtotal} className="flex justify-between">
              <span>Hospedagem</span>
              <strong>{brl(cotacao.subtotal_hospedagem)}</strong>
            </div>
            {cotacao.cafe_valor_total > 0 && (
              <div data-testid={T.cotacaoCafe} className="flex justify-between">
                <span>Café ({cotacao.cafe_tipo})</span>
                <strong>{brl(cotacao.cafe_valor_total)}</strong>
              </div>
            )}
          </div>

          <hr className="border-stone-300/60" />

          <div data-testid={T.cotacaoTotal} className="flex justify-between items-baseline">
            <span className="text-sm text-stone-600">Total estimado</span>
            <span className="font-public-title text-2xl text-[#083A6B]">{brl(cotacao.total)}</span>
          </div>

          <p className="text-[11px] text-stone-500 leading-relaxed">
            Valores calculados pelo nosso sistema. A confirmação final e a forma de
            pagamento são combinadas por nossa equipe após o envio da pré-reserva.
          </p>

          <button
            type="button"
            data-testid={T.cotacaoVoltar}
            onClick={onVoltar}
            className="text-xs text-stone-600 hover:text-[#083A6B] underline underline-offset-2"
          >
            Alterar datas / hóspedes
          </button>
        </div>
      </aside>

      {/* Formulário do hóspede */}
      <div className="lg:col-span-3 lg:order-1 rounded-xl border border-stone-200 bg-white p-6 lg:p-8">
        <div className="mb-6">
          <h2 className="font-public-title text-xl text-stone-900">Seus dados</h2>
          <p className="text-sm text-stone-600 mt-1">
            Precisamos dessas informações para confirmar sua pré-reserva.
          </p>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
          className="space-y-4"
          noValidate
        >
          <Field
            id={T.hospedeNome} label="Nome completo"
            value={form.nome}
            onChange={(v) => set("nome", v)}
            error={formErr.nome} required autoComplete="name"
          />
          <Field
            id={T.hospedeCpf} label="CPF"
            value={form.cpf}
            onChange={(v) => set("cpf", maskCPF(v))}
            error={formErr.cpf} required
            placeholder="000.000.000-00"
            inputMode="numeric" maxLength={14}
          />
          <Field
            id={T.hospedeEmail} label="E-mail"
            value={form.email}
            onChange={(v) => set("email", v)}
            error={formErr.email} required type="email"
            autoComplete="email" inputMode="email"
            placeholder="seu@email.com"
          />
          <Field
            id={T.hospedeTelefone} label="Telefone / WhatsApp"
            value={form.telefone}
            onChange={(v) => set("telefone", maskTelefone(v))}
            error={formErr.telefone} required
            autoComplete="tel" inputMode="tel" maxLength={16}
            placeholder="(11) 98765-4321"
          />

          <div>
            <Label htmlFor={T.hospedeObservacoes} className="text-sm font-medium text-stone-700">
              Observações (opcional)
            </Label>
            <Textarea
              id={T.hospedeObservacoes}
              data-testid={T.hospedeObservacoes}
              value={form.observacoes}
              onChange={(e) => set("observacoes", e.target.value.slice(0, 500))}
              placeholder="Alguma preferência, pedido especial ou horário aproximado de chegada?"
              rows={4}
              maxLength={500}
              className="mt-1.5"
            />
            <div className="flex justify-between mt-1 text-xs text-stone-500">
              <span>{formErr.observacoes || ""}</span>
              <span>{form.observacoes.length}/500</span>
            </div>
          </div>

          {/* Honeypot — invisível para humanos, bots preenchem. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
            <label htmlFor={T.honeypot}>Website (não preencher)</label>
            <input
              id={T.honeypot}
              data-testid={T.honeypot}
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => set("website", e.target.value)}
            />
          </div>

          <div className="pt-2 flex items-start gap-3">
            <Checkbox
              id={T.aceiteCheckbox}
              data-testid={T.aceiteCheckbox}
              checked={form.aceite}
              onCheckedChange={(v) => set("aceite", v === true)}
              className="mt-1"
            />
            <Label htmlFor={T.aceiteCheckbox} className="text-sm text-stone-700 leading-snug">
              Entendo que esta é uma <strong>pré-reserva</strong> e que a hospedagem
              só será confirmada após contato da equipe.
              {formErr.aceite && <span className="block text-red-600 text-xs mt-1">{formErr.aceite}</span>}
            </Label>
          </div>

          {submitErr && (
            <div
              data-testid={T.formError}
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2"
            >
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> {submitErr}
            </div>
          )}

          <div className="pt-2 flex flex-col sm:flex-row-reverse gap-3">
            <Button
              type="submit"
              data-testid={T.submit}
              disabled={submitting}
              className="h-12 px-6 bg-[#083A6B] hover:bg-[#052B50] text-white"
            >
              {submitting ? (
                <><Loader2 data-testid={T.submitLoading} size={16} className="animate-spin mr-2" /> Enviando pré-reserva…</>
              ) : (
                <>Enviar pré-reserva <ArrowRight size={14} className="ml-2" /></>
              )}
            </Button>
            <CTAWhatsApp
              variant="secondary"
              context={ctxWa}
              label="Prefiro falar no WhatsApp"
              testid="reservar-form-whatsapp"
            />
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ id, label, value, onChange, error, required, placeholder,
                 type = "text", autoComplete, inputMode, maxLength }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-medium text-stone-700">
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      <Input
        id={id} data-testid={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        aria-required={required ? "true" : undefined}
        aria-invalid={error ? "true" : undefined}
        className={`mt-1.5 ${error ? "border-red-500" : ""}`}
      />
      {error && <p className="text-red-600 text-xs mt-1" role="alert">{error}</p>}
    </div>
  );
}

// =====================================================================
// STEP 3 — Sucesso (payload do 201 — nenhum GET público — D1=A)
// =====================================================================
function Step3({ reserva, quarto, pousada, ctxWa, onNovaReserva }) {
  const idCurto = (reserva.reserva_id || "").slice(-8).toUpperCase();
  return (
    <div data-testid={T.step3} className="rounded-xl border border-stone-200 bg-white p-6 lg:p-10 text-center space-y-6">
      <div className="w-16 h-16 rounded-full bg-[#083A6B]/10 text-[#083A6B] flex items-center justify-center mx-auto">
        <CheckCircle2 size={32} />
      </div>
      <div>
        <h2
          data-testid={T.sucessoTitulo}
          className="font-public-title text-2xl lg:text-3xl text-stone-900 leading-tight"
        >
          Recebemos sua pré-reserva!
        </h2>
        <p className="text-sm text-stone-600 mt-3 max-w-xl mx-auto leading-relaxed">
          Nossa equipe irá verificar a disponibilidade e entrar em contato{" "}
          <strong>em breve</strong> por e-mail e WhatsApp para confirmar sua hospedagem.
        </p>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 inline-flex items-start gap-2 text-left max-w-xl mx-auto">
        <Info size={16} className="mt-0.5 flex-shrink-0" />
        <span>
          <strong>Atenção:</strong> esta é uma pré-reserva e ainda não representa a
          confirmação definitiva da hospedagem.
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-3 max-w-sm mx-auto text-sm">
        <dt className="text-stone-500 text-right">Protocolo</dt>
        <dd data-testid={T.sucessoProtocolo} className="font-mono text-stone-900 text-left">{idCurto}</dd>
        <dt className="text-stone-500 text-right">Quarto</dt>
        <dd className="text-stone-900 text-left">{quarto.numero}{pousada && ` · ${pousada.nome}`}</dd>
        <dt className="text-stone-500 text-right">Status</dt>
        <dd className="text-left"><span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-[#083A6B]/10 text-[#083A6B] text-xs font-medium">Pré-reserva</span></dd>
        <dt className="text-stone-500 text-right">Total estimado</dt>
        <dd data-testid={T.sucessoTotal} className="font-public-title text-[#083A6B] text-left">{brl(reserva.valor_total)}</dd>
      </dl>

      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
        <CTAWhatsApp
          variant="primary"
          context={ctxWa}
          label="Falar no WhatsApp"
          testid={T.sucessoWhatsapp}
        />
        <Button
          type="button"
          data-testid={T.sucessoNovaReserva}
          variant="outline"
          onClick={onNovaReserva}
          className="h-11 px-5"
        >
          Ver outras pousadas
        </Button>
      </div>
    </div>
  );
}

// =====================================================================
// Panel de indisponibilidade (409 / disponivel=false)
// =====================================================================
const MOTIVO_LABEL = {
  capacidade_insuficiente: "O quarto não comporta esse número de hóspedes.",
  quarto_bloqueado: "O quarto está temporariamente indisponível para reserva.",
  conflito_reserva: "Essas datas já estão reservadas.",
  chacara_inteira_bloqueada: "A chácara está reservada por inteiro nesse período.",
  pousada_inteira_bloqueada: "A pousada está reservada por inteiro nesse período.",
};

function UnavailablePanel({ unavailable, ctxWa, onVoltar }) {
  const label = MOTIVO_LABEL[unavailable.motivo] || "Período indisponível para este quarto.";
  return (
    <div
      data-testid={T.unavailable}
      className="rounded-xl border border-amber-200 bg-amber-50 p-6 lg:p-8 text-center space-y-5"
    >
      <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
        <AlertTriangle size={28} />
      </div>
      <div>
        <h2 className="font-public-title text-xl lg:text-2xl text-stone-900 leading-tight">
          Período indisponível
        </h2>
        <p data-testid={T.unavailableMotivo} className="text-sm text-stone-700 mt-2 max-w-lg mx-auto">
          {label}
          {unavailable.detalhe && <span className="block text-xs text-stone-500 mt-1">{unavailable.detalhe}</span>}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
        <Button
          type="button"
          data-testid={T.unavailableVoltar}
          onClick={onVoltar}
          variant="outline"
          className="h-11 px-5"
        >
          <ArrowLeft size={14} className="mr-2" /> Tentar outras datas
        </Button>
        <CTAWhatsApp
          variant="primary"
          context={ctxWa}
          label="Falar no WhatsApp"
          testid={T.unavailableWhatsapp}
        />
      </div>
    </div>
  );
}
